import {IArticleTarget} from '@app-types/article.types';

const SWEDISH_SITE = 'https://www.varannanvecka.app';
const ENGLISH_SITE = 'https://www.co-parenting.app';

export const isSwedish = (language: string) => /^(se|sv)(-|$)/i.test(language);
export const knowledgeTitle = (language: string) => isSwedish(language) ? 'Kunskap' : 'Knowledge';
export const knowledgeRoot = (language: string, child = false) =>
  isSwedish(language) ? `/kunskap/${child ? 'for-barn/' : ''}` : `/knowledge/${child ? 'for-children/' : ''}`;

// Chat messages retain their original IDs after the account changes language.
const HELP_TRANSLATIONS = [['7069', '7084'], ['3023', '3786'], ['2249', '2601'], ['5865', '6826'], ['8203', '8204']];

export function articleReference(location: {pathname: string}): string | undefined {
  const match = location.pathname.match(/^\/app\/knowledge\/([^/]+)\/?$/);
  if (!match) return undefined;
  try {
    const reference = decodeURIComponent(match[1]);
    return /^[\w-]+$/.test(reference) ? reference : undefined;
  } catch {
    return undefined;
  }
}

export function articleUrl(reference: string, language: string, country?: string): string {
  const pair = HELP_TRANSLATIONS.find(ids => ids.includes(reference));
  const id = pair ? pair[isSwedish(language) ? 0 : 1] : reference;
  // This help article has never had an English translation.
  const swedishOnly = reference === '596' || reference === 'sa-fungerar-varannan-vecka-appen';
  const site = swedishOnly || isSwedish(language) ? SWEDISH_SITE : ENGLISH_SITE;
  const query = /^(us|uk|ca)$/.test(country || '') ? `?country=${country}` : '';
  return `${site}/embed/${encodeURIComponent(id)}/${query}`;
}

export function browsingUrl(language: string, requested?: string, child = false, country?: string): string {
  const root = knowledgeRoot(language);
  const childRoot = knowledgeRoot(language, child);
  const path = requested && requested.startsWith(childRoot) && !/[?#\\%]|\.\./.test(requested) ? requested : childRoot;
  const site = isSwedish(language) ? SWEDISH_SITE : ENGLISH_SITE;
  return `${site}/embed/browse/${path.slice(root.length)}${/^(us|uk|ca)$/.test(country || '') ? `?country=${country}` : ''}`;
}

// Legacy URLs are accepted as input only. Callers open a modal with the returned ID/slug.
export function articleTargetForLink(value: string): IArticleTarget | undefined {
  try {
    const address = value.replace(/[.,;!?]+$/, '');
    const url = new URL(address.startsWith('www.') ? `https://${address}` : address, ENGLISH_SITE);
    if (!/^(www\.)?(co-parenting|varannanvecka)\.app$/.test(url.hostname)
      || !/^https?:$/.test(url.protocol)) return undefined;
    const articlePath = url.pathname.match(/^\/(?:artikel|article)\/(.+?)\/?$/)?.[1];
    const reference = articleReference(url)
      || (articlePath && articlePath.replace(/\//g, '-'))
      || url.pathname.match(/^\/embed\/([^/]+)\/?$/)?.[1];
    if (!reference || reference === 'browse') return undefined;
    const decoded = decodeURIComponent(reference);
    if (!/^[\w-]+$/.test(decoded)) return undefined;
    const country = url.searchParams.get('country') || undefined;
    return {reference: decoded, country: /^(us|uk|ca)$/.test(country || '') ? country : undefined};
  } catch {
    return undefined;
  }
}

export interface IEmbedNavigation {
  kind: 'article' | 'browse' | 'app';
  value: string;
  country?: string;
}

export function validatedEmbedNavigation(navigation: IEmbedNavigation): IEmbedNavigation | undefined {
  const {kind, value, country} = navigation;
  if (typeof value !== 'string') return undefined;
  if (kind === 'article' && /^[\w-]+$/.test(value)) {
    return {kind, value, country: /^(us|uk|ca)$/.test(country || '') ? country : undefined};
  }
  if (kind === 'browse' && /^\/(?:knowledge|kunskap)\//.test(value) && !/[?#\\%]|\.\./.test(value)) {
    return {kind, value, country: /^(us|uk|ca)$/.test(country || '') ? country : undefined};
  }
  if (kind === 'app' && /^\/app(?:\/|$)/.test(value) && !/[\\%]|\.\./.test(value)) {
    const target = articleTargetForLink(value);
    return target ? {kind: 'article', value: target.reference, country: target.country} : {kind, value};
  }
  return undefined;
}



// WEBPACK FOOTER //
// ./src/containers/knowledge/articleNavigation.ts